import * as css from 'css/exports.module.scss';

import './TimelineSynopsis.scss';

import { ChangeRecordsFilterParams } from './filters/ChangeRecordsFilter';
import { getChangeRecordClassName } from './@arch/lib';
import { getTimelineSynopsisSelector } from './@arch/selectors';

import { ArrowDownOutlined, ArrowUpOutlined, SwapOutlined } from '@ant-design/icons';
import { Col, Row, Statistic } from 'antd';
import { COPY_GENERIC, TIMELINE_COPY } from '@sonar-software/sonar-ts-constants';
import { Else, If, Then, When } from 'react-if';
import React, { useMemo } from 'react';
import { TimelineChangeType, TimelineSynopsisResponse } from '@sonar-software/sonar-core';
import { UpdateQueryParams, useCurrentOrganization, useSonarSelector } from '@hooks';

const emptyResponse: TimelineSynopsisResponse = {
	additions: 0,
	admins: 0,
	automations: 0,
	deletions: 0,
	fields: 0,
	modifications: 0,
	objects: 0
};

export interface TimelineSynopsisProps {
	hideSystem?: boolean;
	onChange: UpdateQueryParams<ChangeRecordsFilterParams>;
	onChangeTypeClicked: (changeType: TimelineChangeType) => void;
	onChangeTypeHovered: (changeType: TimelineChangeType) => void;
}

export const TimelineSynopsis: React.FC<TimelineSynopsisProps> = ({
	hideSystem,
	onChangeTypeClicked,
	onChangeTypeHovered
}) => {
	const synopsis = useSonarSelector(getTimelineSynopsisSelector.data);

	const data: TimelineSynopsisResponse = useMemo(() => ({
		...emptyResponse,
		...synopsis
	}), [ synopsis ]);

	const organization = useCurrentOrganization();
	const hasEntities = data.objects !== 0 || data.fields !== 0 || data.automations !== 0;
	const hasChanges = data.additions !== 0 || data.modifications !== 0 || data.deletions !== 0;

	return (
		<div className='timeline-synopsis'>
			<div className='statistics-section'>
				<Row
					gutter={ [ Number(css.size24), Number(css.size24) ] }
					wrap={ false }>
					<Col key='admins'>
						<Statistic
							title='Admins'
							value={ data.admins } />
					</Col>
					<Col
						key='additions'
						onClick={ onChangeTypeClicked.bind(null, TimelineChangeType.Discovered) }
						onMouseEnter={ onChangeTypeHovered.bind(null, TimelineChangeType.Discovered) }
						onMouseLeave={ onChangeTypeHovered.bind(null, undefined) }>
						<Statistic
							className={ getChangeRecordClassName(TimelineChangeType.Discovered, data.additions) }
							prefix={ <ArrowUpOutlined /> }
							title='Additions'
							value={ data.additions } />
					</Col>
					<Col
						key='modifications'
						onClick={ onChangeTypeClicked.bind(null, TimelineChangeType.Modified) }
						onMouseEnter={ onChangeTypeHovered.bind(null, TimelineChangeType.Modified) }
						onMouseLeave={ onChangeTypeHovered.bind(null, undefined) }>
						<Statistic
							className={ getChangeRecordClassName(TimelineChangeType.Modified, data.modifications) }
							prefix={ <SwapOutlined /> }
							title='Modifications'
							value={ data.modifications } />
					</Col>
					<Col
						key='deletions'
						onClick={ onChangeTypeClicked.bind(null, TimelineChangeType.Vanished) }
						onMouseEnter={ onChangeTypeHovered.bind(null, TimelineChangeType.Vanished) }
						onMouseLeave={ onChangeTypeHovered.bind(null, undefined) }>
						<Statistic
							className={ getChangeRecordClassName(TimelineChangeType.Vanished, data.deletions) }
							prefix={ <ArrowDownOutlined /> }
							title='Deletions'
							value={ data.deletions } />
					</Col>
				</Row>
			</div>
			<div className='message'>
				<When condition={ hideSystem }>
					{ TIMELINE_COPY.STATISTICS_EXCLUDING }
				</When>
				<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_ADMIN_COUNT(data.admins) }</span>
				<If condition={ data.admins === 1 }>
					<Then>{ TIMELINE_COPY.STATISTICS_HAS_MADE }</Then>
					<Else>{ TIMELINE_COPY.STATISTICS_HAVE_MADE }</Else>
				</If>
				<span className='highlighted'>
					{ TIMELINE_COPY.STATISTICS_CHANGE_COUNT(data.additions + data.modifications + data.deletions) }
				</span>
				{ TIMELINE_COPY.STATISTICS_TO }
				<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_ORGANIZATION(organization?.name ?? '') }</span>
				{ TIMELINE_COPY.STATISTICS_PERIOD }
				<When condition={ hasEntities || hasChanges }>{ TIMELINE_COPY.STATISTICS_THIS_INCLUDES }</When>
				<When condition={ data.objects !== 0 }>
					<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_OBJECT_COUNT(data.objects) }</span>
				</When>
				<When condition={ data.fields !== 0 }>
					<When condition={ data.objects !== 0 }>
						<If condition={ data.automations === 0 }>
							<Then>{ COPY_GENERIC.AND_SPACED }</Then>
							<Else>{ TIMELINE_COPY.STATISTICS_COMMA }</Else>
						</If>
					</When>
					<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_FIELD_COUNT(data.fields) }</span>
				</When>
				<When condition={ data.automations !== 0 }>
					<When condition={ data.objects !== 0 && data.fields !== 0 }>
						{ TIMELINE_COPY.STATISTICS_COMMA }
					</When>
					<When condition={ data.objects !== 0 || data.fields !== 0 }>{ COPY_GENERIC.AND_SPACED }</When>
					<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_AUTOMATION_COUNT(data.automations) }</span>
				</When>
				<When condition={ hasEntities && hasChanges }>{ TIMELINE_COPY.STATISTICS_WITH }</When>
				<When condition={ data.additions !== 0 }>
					<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_ADDITIONS(data.additions) }</span>
				</When>
				<When condition={ data.modifications !== 0 }>
					<When condition={ data.additions !== 0 }>
						<If condition={ data.deletions === 0 }>
							<Then>{ COPY_GENERIC.AND_SPACED }</Then>
							<Else>{ TIMELINE_COPY.STATISTICS_COMMA }</Else>
						</If>
					</When>
					<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_MODIFICATIONS(data.modifications) }</span>
				</When>
				<When condition={ data.deletions !== 0 }>
					<When condition={ data.additions !== 0 && data.modifications !== 0 }>
						{ TIMELINE_COPY.STATISTICS_COMMA }
					</When>
					<When condition={ data.additions !== 0 || data.modifications !== 0 }>
						{ COPY_GENERIC.AND_SPACED }
					</When>
					<span className='highlighted'>{ TIMELINE_COPY.STATISTICS_DELETIONS(data.deletions) }</span>
				</When>
				<When condition={ data.additions !== 0 || data.modifications !== 0 || data.deletions !== 0 }>
					{ TIMELINE_COPY.STATISTICS_PERIOD }
				</When>
			</div>
		</div>
	);
};
